import React, { useMemo, useState } from 'react';
import { BellIcon, LockIcon } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../SectionHeading';

// STORE CLOSED UNTIL LAUNCH (Ryan, Aug 30). The Shopify catalogue is fully
// drafted, so every /products/<handle> URL 404s and the storefront is empty.
// The tiles below are NON-CLICKABLE previews, not links. Do NOT re-add the
// product anchor or the SHOP ASTROSWAG button until the store is live again.
// See FAR_Store_Disabled_Until_Launch.md for the restore list.
//
// FULL LINE RESTORED (Ryan, Aug 30): "put up everything except the racing theme
// items and the metal hat." Deliberately absent: Racing Cap, Racing Iron-On
// Patch, Distressed Metal Dad Hat, and the three [DUPLICATE] products.
//
// 31 of the 34 restored products appear here. THREE ARE MISSING BECAUSE THEY
// HAVE NO SHOP CARD IN CLOUDINARY (verified: only 34 far-shop-* assets exist,
// and they map to the original list):
//   - MagSafe clear case for iPhone
//   - Future Astronaut AirPods Case
//   - Unisex Champion tie-dye hoodie
// Ask Caden for far-shop-magsafe, far-shop-faairpods and far-shop-champhoodie,
// then add the three rows. Do not point them at another product's key.
//
// Handles and prices verified against Shopify on 2026-08-30 so this list can go
// straight back to links when the store reopens.

const CLD = (key: string) => `https://res.cloudinary.com/kn1rv77n/image/upload/e_background_removal/f_auto,q_auto,w_320/far-shop-${key}.png`;
type Cat = 'all' | 'apparel' | 'hats' | 'acc';
const TABS: {
  key: Cat;
  label: string;
  dot?: string;
}[] = [{
  key: 'all',
  label: 'ALL'
}, {
  key: 'apparel',
  label: 'APPAREL',
  dot: '#8b5cff'
}, {
  key: 'hats',
  label: 'HATS',
  dot: '#37e5ff'
}, {
  key: 'acc',
  label: 'ACCESSORIES',
  dot: '#c86bff'
}];
const GLOW: Record<string, string> = {
  apparel: 'rgba(139,92,255,0.30)',
  hats: 'rgba(55,229,255,0.20)',
  acc: 'rgba(200,107,255,0.26)'
};
const PRODUCTS = [{
  title: 'Basic Hoodie',
  price: '$66',
  handle: 'basic-hoodie',
  cat: 'apparel',
  key: 'hoodie'
}, {
  title: 'Textured Hoodie',
  price: '$75',
  handle: 'unisex-champion-tie-dye-hoodie',
  cat: 'apparel',
  key: 'texhoodie'
}, {
  title: 'Baseball Jersey Black',
  price: '$72',
  handle: 'baseball-jersey-black',
  cat: 'apparel',
  key: 'jerseyb'
}, {
  title: 'Baseball Jersey White',
  price: '$72',
  handle: 'baseball-jersey-white',
  cat: 'apparel',
  key: 'jerseyw'
}, {
  title: 'Faded Tee',
  price: '$40',
  handle: 'oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'fadedtee'
}, {
  title: 'Tie-Dye Tee',
  price: '$40',
  handle: 'oversized-tie-dye-t-shirt',
  cat: 'apparel',
  key: 'tiedyetee'
}, {
  title: 'Alien Reflection Tee',
  price: '$36',
  handle: 'alien-reflection-oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'alientee'
}, {
  title: 'Performance Tee',
  price: '$29',
  handle: 'unisex-performance-crew-neck-t-shirt',
  cat: 'apparel',
  key: 'perftee'
}, {
  title: 'Rocket Dad Hat',
  price: '$30',
  handle: 'dad-hat',
  cat: 'hats',
  key: 'rocketdad'
}, {
  title: 'OG Dad Hat',
  price: '$30',
  handle: 'og-logo-dad-hat',
  cat: 'hats',
  key: 'ogdad'
}, {
  title: 'FAR Snapback',
  price: '$30',
  handle: 'flat-bill-cap-1',
  cat: 'hats',
  key: 'snapback'
}, {
  title: 'Flat Bill Cap',
  price: '$28',
  handle: 'flat-bill-cap',
  cat: 'hats',
  key: 'flatbill'
}, {
  title: 'Vintage Cap',
  price: '$30',
  handle: 'vintage-cap',
  cat: 'hats',
  key: 'vintage'
}, {
  title: 'FAR Rope Cap',
  price: '$25',
  handle: 'future-astronaut-cap-black',
  cat: 'hats',
  key: 'ropecap'
}, {
  title: 'Rocket Beanie',
  price: '$30',
  handle: 'embroidered-beanie',
  cat: 'hats',
  key: 'rocketbeanie'
}, {
  title: 'OG Beanie',
  price: '$30',
  handle: 'og-logo-beanie',
  cat: 'hats',
  key: 'ogbeanie'
}, {
  title: 'AOP Bucket Hat',
  price: '$32',
  handle: 'bucket-hat-aop',
  cat: 'hats',
  key: 'aopbucket'
}, {
  title: 'Astronaut Bucket Hat',
  price: '$32',
  handle: 'astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'astrobucket'
}, {
  title: 'Alien Bucket Hat',
  price: '$32',
  handle: 'alien-pattern-bucket-hat',
  cat: 'hats',
  key: 'alienbucket'
}, {
  title: 'Alien Party Bucket Hat',
  price: '$25',
  handle: 'copy-of-astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'partybucket'
}, {
  title: 'Space Bandana',
  price: '$25',
  handle: 'all-over-print-bandana',
  cat: 'acc',
  key: 'bandana'
}, {
  title: 'Fanny Pack',
  price: '$44',
  handle: 'fanny-pack',
  cat: 'acc',
  key: 'fanny'
}, {
  title: 'Drawstring Bag',
  price: '$20',
  handle: 'drawstring-bag',
  cat: 'acc',
  key: 'drawstring'
}, {
  title: 'Astrology Shawl',
  price: '$62',
  handle: 'astrology-shawl',
  cat: 'acc',
  key: 'shawl'
}, {
  title: 'Astrology Scarf',
  price: '$35',
  handle: 'astrology-scarf',
  cat: 'acc',
  key: 'astroscarf'
}, {
  title: 'Galaxy Scarf',
  price: '$35',
  handle: 'galaxy-scarf',
  cat: 'acc',
  key: 'galaxyscarf'
}, {
  title: 'Space Pashmina',
  price: '$35',
  handle: 'space-scarf',
  cat: 'acc',
  key: 'pashmina'
}, {
  title: 'Space Scarf',
  price: '$40',
  handle: 'space-scarf-2',
  cat: 'acc',
  key: 'spacescarf2'
}, {
  title: 'Apple Watch Band',
  price: '$49.80',
  handle: 'watch-band-for-apple-watch',
  cat: 'acc',
  key: 'watchband'
}, {
  title: 'Rocket AirPods Case',
  price: '$31.50',
  handle: 'rocket-logo-airpods-case',
  cat: 'acc',
  key: 'airpods'
}, {
  title: 'Rocket iPhone Case',
  price: '$16',
  handle: 'rocket-logo-iphone-case',
  cat: 'acc',
  key: 'iphonecase'
}];
export function AstroSwag() {
  const [cat, setCat] = useState<Cat>('all');
  const shown = useMemo(() => PRODUCTS.filter(p => cat === 'all' || p.cat === cat), [cat]);
  return <section id="astroswag" className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-id="element-82" data-mp="1aienxl8cspn8">
      <SectionHeading eyebrow="MERCH" title={<>
            Future <span className="text-gradient" data-id="element-84" data-mp="1k8hg5j7nhzki">AstroSwag</span>
          </>} description="Shop's shut while we finish the first drop. Here's the whole rack." data-id="element-83" data-mp="1fbqywsk40xxi" />

      <Reveal data-id="element-85" data-mp="1t9pbptis8maw">
        <div className="mt-7 rounded-2xl bg-gradient-to-r from-[#37e5ff55] via-[#8b5cff55] to-[#c86bff55] p-px shadow-[0_0_44px_rgba(139,92,255,0.14)]" data-id="element-86" data-mp="1tb8txpo017fb">
          <div className="overflow-hidden rounded-[calc(1rem-1px)] bg-[#07051a]/95" data-id="element-87" data-mp="3q8h1f4g39s2h">
            <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-88" data-mp="3l9nobd4ps32u">
              {TABS.map(t => <button key={t.key} type="button" onClick={() => setCat(t.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-tech text-[0.56rem] font-semibold tracking-[0.12em] transition-colors duration-200 sm:text-[0.6rem] ${cat === t.key ? 'border-transparent bg-gradient-to-r from-[#37e5ff] via-[#8b5cff] to-[#c86bff] text-[#050414] shadow-neon' : 'border-white/15 text-white/60 hover:border-violet/50 hover:text-white'}`} data-id="element-89" data-mp="3uc6rxzgbqjq5">
                  {t.dot && cat !== t.key && <span className="h-1.5 w-1.5 rounded-full" style={{
                background: t.dot
              }} aria-hidden="true" data-id="element-90" data-mp="29hy6yzohktnv" />}
                  {t.label}
                </button>)}
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/[0.07] px-3 py-1 font-tech text-[0.52rem] font-semibold tracking-[0.14em] text-cyan/90 sm:text-[0.56rem]" data-id="element-91" data-mp="1ztg7qmvuuumc">
                <LockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-92" data-mp="1ptpdmmb7ihms" />
                OPENS JANUARY 2027
              </span>
            </div>

            <div className="scp-list grid max-h-[47vh] grid-cols-3 gap-2 overflow-y-auto overscroll-contain p-2.5 lg:max-h-[48vh] lg:grid-cols-6 lg:gap-2.5 lg:p-3.5" data-id="element-93" data-mp="1gtshy0qegii8">
              {shown.map(p => <div key={p.handle} className="flex cursor-default flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0a0720] opacity-90" data-id="element-94" data-mp="3b7tqxefnylw1">
                  <span className="block aspect-square overflow-hidden" style={{
                background: `radial-gradient(120% 95% at 50% 32%, ${GLOW[p.cat]}, rgba(5,4,20,0) 72%), linear-gradient(180deg, #0d0925 0%, #070517 100%)`
              }} data-id="element-95" data-mp="slog9l9od6kq">
                    <img src={CLD(p.key)} alt={p.title} loading="lazy" className="h-full w-full object-contain p-2 drop-shadow-[0_12px_16px_rgba(0,0,0,0.6)]" data-id="element-96" data-mp="evcib5qesdlo" />
                  </span>
                  <span className="px-2 pt-1.5 font-display text-[0.46rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white lg:text-[0.5rem]" data-id="element-97" data-mp="3w1wlcq7ngh86">
                    {p.title}
                  </span>
                  <span className="px-2 pb-1.5 pt-0.5 font-tech text-[0.52rem] text-cyan lg:text-[0.56rem]" data-id="element-98" data-mp="1ua9yn0usj8hi">{p.price}</span>
                </div>)}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15} data-id="element-99" data-mp="3872xxvl6xnfk">
        <div className="mt-5 flex flex-col items-center gap-2 lg:mt-6" data-id="element-100" data-mp="3d6fw3chiigxg">
          <a href="#contact" className="group inline-flex items-center gap-2 rounded-full bg-violet px-7 py-3.5 font-tech text-xs font-semibold tracking-[0.16em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft" data-id="element-101" data-mp="3d4lby0e2esuf">
            <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-102" data-mp="2u7k3u6p7scm9" />
            TELL ME WHEN IT DROPS
          </a>
          <p className="font-script text-[0.95rem] text-magenta" data-id="element-103" data-mp="2y9f1n17xs7va">Store opens with the first releases</p>
        </div>
      </Reveal>
    </section>;
}